/**
 * @file use-local-pickup-form-state.ts
 *
 * Controlled-form state for the local-pickup form (no react-hook-form). Holds the
 * editable fields, exposes a `patch` updater, tracks dirtiness against the seed,
 * and validates the required fields (name, street address, city, postcode,
 * country). Errors surface only after the first `validate()` call. Re-seeds only
 * when the seed reference changes (mirroring `use-coupon-form-state`); pass a
 * stable seed reference.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { PickupLocationInput } from '../../api/shop-types';
import {
  derivePickupFields,
  shallowEqualPickup,
} from './local-pickup-form-utils';
import type { PickupFormErrors, PickupFormFields } from './local-pickup-form-types';

/** What {@link useLocalPickupFormState} returns to the form. */
export interface UseLocalPickupFormStateResult {
  fields: PickupFormFields;
  patch: (update: Partial<PickupFormFields>) => void;
  isDirty: boolean;
  errors: PickupFormErrors;
  validate: () => boolean;
  /** Attach to the name field; focused when validation fails. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the local-pickup form's field state and validation.
 *
 * @param seed - The seed pickup location (stable reference; re-seeds on change).
 *
 * @returns The fields, updater, dirty flag, errors, and a `validate` function.
 */
export function useLocalPickupFormState(
  seed: PickupLocationInput,
): UseLocalPickupFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<PickupFormFields>(() => derivePickupFields(seed));
  const initialRef = useRef<PickupFormFields>(fields);
  const seededRef = useRef(seed);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (seededRef.current === seed) {
      return;
    }
    seededRef.current = seed;
    const seeded = derivePickupFields(seed);
    setFields(seeded);
    initialRef.current = seeded;
    setSubmitted(false);
  }, [seed]);

  const patch = useCallback((update: Partial<PickupFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: PickupFormFields): PickupFormErrors => {
      const errors: PickupFormErrors = {};
      if (current.name.trim() === '') {
        errors.nameError = intl.formatMessage(adminMessages.shippingPickupNameRequired);
      }
      if (current.address1.trim() === '') {
        errors.address1Error = intl.formatMessage(
          adminMessages.shippingPickupAddressRequired,
        );
      }
      if (current.city.trim() === '') {
        errors.cityError = intl.formatMessage(adminMessages.shippingPickupCityRequired);
      }
      if (current.postcode.trim() === '') {
        errors.postcodeError = intl.formatMessage(
          adminMessages.shippingPickupPostcodeRequired,
        );
      }
      if (current.country.trim() === '') {
        errors.countryError = intl.formatMessage(
          adminMessages.shippingPickupCountryRequired,
        );
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  const isDirty = useMemo(
    () => !shallowEqualPickup(fields, initialRef.current),
    [fields],
  );

  return { fields, patch, isDirty, errors, validate, firstErrorRef };
}
